/**
 * useChatRealTimeUpdates Hook
 *
 * Chat-specific hook that wraps useRealTimeUpdates and organizes events by exchange ID.
 * Organizes events into two categories:
 * - byExchangeId: Events for exchanges that the frontend knows about
 * - pending: Events for exchanges that exist in claims-ai but not in frontend yet
 *
 * Historical events (from initial Firestore snapshot) are only included if they
 * belong to known exchanges. This prevents stale pending events from appearing
 * when loading an old chat.
 */

import { useMemo } from 'react';

import type { IssueTokenResponse } from '~/components/AiChat/types';

import { type RealTimeEvent, useRealTimeUpdates } from './useRealTimeUpdates';

export interface OrganizedChatUpdates {
  byExchangeId: Record<string, RealTimeEvent[]>;
  pending: RealTimeEvent[];
}

/** All the hook reads of an exchange: events are matched to it by id. */
interface ExchangeWithId {
  id: string | number;
}

export interface UseChatRealTimeUpdatesOptions {
  issueTokenResponse?: IssueTokenResponse | null;
  exchanges?: ExchangeWithId[];
  enabled?: boolean;
}

export interface UseChatRealTimeUpdatesReturn {
  events: RealTimeEvent[];
  organizedUpdates: OrganizedChatUpdates;
  isConnected: boolean;
  error: string | null;
  clearEvents: () => void;
}

const organizeEventsByExchange = (events: RealTimeEvent[], exchanges: ExchangeWithId[]): OrganizedChatUpdates => {
  const knownExchangeIds = new Set(exchanges.map((e) => String(e.id)));
  const byExchangeId: Record<string, RealTimeEvent[]> = {};
  const pending: RealTimeEvent[] = [];

  for (const event of events) {
    const exchangeId = event.chat_exchange_id;
    const belongsToKnownExchange = exchangeId && knownExchangeIds.has(exchangeId);

    if (belongsToKnownExchange) {
      // Event belongs to a known exchange - add to byExchangeId
      (byExchangeId[exchangeId] ??= []).push(event);
    } else if (!event._isHistorical) {
      // Real-time event for unknown exchange - add to pending
      // (Historical events for unknown exchanges are filtered out)
      pending.push(event);
    }
  }

  return { byExchangeId, pending };
};

export function useChatRealTimeUpdates({
  issueTokenResponse = null,
  exchanges = [],
  enabled = true,
}: UseChatRealTimeUpdatesOptions): UseChatRealTimeUpdatesReturn {
  const { events, isConnected, error, clearEvents } = useRealTimeUpdates({
    issueTokenResponse,
    enabled,
  });

  const organizedUpdates = useMemo(() => organizeEventsByExchange(events, exchanges), [events, exchanges]);

  return { events, organizedUpdates, isConnected, error, clearEvents };
}
